import { Box, Divider, Flex, Icon, Label, Text } from '@ttoss/ui';
import {
  Form,
  FormFieldCheckbox,
  FormFieldRadio,
  useForm,
  yup,
  yupResolver,
} from '@ttoss/forms';
import { Modal } from '@ttoss/components';
import Button from '../Button/Button';
import React from 'react';

const DISTANCE_OPTIONS = [
  { label: 'até 50km', value: '50' },
  { label: 'até 100km', value: '100' },
  { label: 'até 200km', value: '200' },
  { label: 'até 250km', value: '250' },
  { label: '+ de 300km', value: '300' },
];

const CheckboxesContainer: React.FC = ({ children }) => {
  return (
    <Flex
      className="CheckboxesContainer"
      sx={{
        flexDirection: 'column',
        gap: 6,
        'div>div>div+label': {
          fontSize: 3,
        },
        'div>div>div>input[type="checkbox"]+svg, div>div>div>input[type="checkbox"]+svg+svg':
          {
            width: 18,
            height: 18,
          },
      }}
    >
      {children}
    </Flex>
  );
};

type FormFilter = {
  distance: string;
  woodProducers?: boolean;
  woodBuyers?: boolean;
  eucalipto?: boolean;
  pinus?: boolean;
  cedroAustraliano?: boolean;
  mognoAfricano?: boolean;
  teca?: boolean;
  planted?: boolean;
  cut?: boolean;
  futureCut?: boolean;
};

const schema = yup.object({
  distance: yup.string().required('O distância é obrigatório'),
});

type FilterModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onFilter: (filter: FormFilter) => void;
  title: string;
  type: 'buyer' | 'provider';
};

export const FilterModal = ({
  isOpen,
  onClose,
  onFilter,
  title,
  type,
}: FilterModalProps) => {
  const formMethods = useForm<FormFilter>({
    mode: 'all',
    resolver: yupResolver(schema) as any,
    defaultValues:
      type === 'provider'
        ? {
            distance: '',
            woodProducers: true,
            woodBuyers: false,
            eucalipto: true,
            pinus: true,
            cedroAustraliano: true,
            mognoAfricano: true,
            teca: true,
            planted: true,
            cut: true,
            futureCut: true,
          }
        : {
            distance: '',
          },
  });

  const handleSubmit = (data: FormFilter) => {
    onFilter(data);
  };

  return (
    <Modal
      isOpen={isOpen}
      style={{
        content: {
          border: 0,
          padding: 0,
          background: 'transparent',
          maxHeight: '100vh',
          outline: 0,
          overflowY: 'hidden',
        },
        overlay: {
          padding: 0,
          zIndex: 100,
        },
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          width: ['100vw', 'unset'],
          minWidth: ['unset', '600px'],
          borderRadius: ['unset', '10px'],
          backgroundColor: 'background',
          overflow: ['hidden', 'hidden'],
          overflowY: 'hidden',
          minHeight: ['100vh', '60vh'],
          marginY: [0, 6],
        }}
      >
        <Flex
          sx={{
            position: 'relative',
            height: '91px',
            backgroundColor: 'secondary',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Icon
            onClick={onClose}
            sx={{
              position: 'absolute',
              left: 7,
              fontSize: 5,
              lineHeight: 0,
              alignSelf: 'center',
              color: 'white',
              display: ['unset', 'none'],
              cursor: 'pointer',
            }}
            icon="ic:round-keyboard-arrow-left"
          />

          <Text sx={{ color: 'white', fontSize: 3 }}>{title}</Text>

          <Icon
            onClick={onClose}
            role="button"
            sx={{
              position: 'absolute',
              right: 7,
              fontSize: 5,
              alignSelf: 'center',
              color: 'white',
              display: ['none', 'unset'],
              cursor: 'pointer',
            }}
            icon="ic:round-close"
          />
        </Flex>

        <Form
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            flex: 1,
          }}
          {...formMethods}
          onSubmit={handleSubmit}
        >
          <Flex
            sx={{
              flexDirection: 'column',
              height: '100%',
              flex: 1,
              paddingX: 6,
            }}
          >
            <Flex
              sx={{
                flexDirection: 'column',
                paddingX: 5,
                paddingTop: 8,
                paddingBottom: [9, 8],
                display: ['flex', 'grid'],
                gridTemplateColumns: '1fr 1fr',
              }}
            >
              <Box>
                <Label
                  sx={{ color: 'dark', marginBottom: 6, fontFamily: 'heading' }}
                >
                  Distância
                </Label>
                <Box
                  className="aqui"
                  sx={{
                    'div>div:has(label)': {
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                    },
                  }}
                >
                  <FormFieldRadio
                    name="distance"
                    label=""
                    options={DISTANCE_OPTIONS}
                  />
                </Box>
              </Box>

              {type === 'provider' && (
                <>
                  <Box>
                    <Label
                      sx={{
                        color: 'dark',
                        marginBottom: 6,
                        marginTop: [8, 0],
                        fontFamily: 'heading',
                      }}
                    >
                      Tipo de negócio
                    </Label>

                    <CheckboxesContainer>
                      <FormFieldCheckbox
                        name="woodProducers"
                        label="Produtores de madeira"
                      />

                      <FormFieldCheckbox
                        name="woodBuyers"
                        label="Compradores de madeira"
                      />
                    </CheckboxesContainer>
                  </Box>

                  <Box>
                    <Label
                      sx={{
                        color: 'dark',
                        marginBottom: 6,
                        marginTop: 8,
                        fontFamily: 'heading',
                      }}
                    >
                      Tipo de Madeira
                    </Label>

                    <CheckboxesContainer>
                      <FormFieldCheckbox name="eucalipto" label="Eucalipto" />

                      <FormFieldCheckbox name="pinus" label="Pinus" />

                      <FormFieldCheckbox
                        name="cedroAustraliano"
                        label="Cedro Australiano"
                      />

                      <FormFieldCheckbox
                        name="mognoAfricano"
                        label="Mogno Africano"
                      />

                      <FormFieldCheckbox name="teca" label="Teca" />
                    </CheckboxesContainer>
                  </Box>

                  <Box>
                    <Label
                      sx={{
                        marginBottom: 6,
                        marginTop: 8,
                        fontFamily: 'heading',
                      }}
                    >
                      Estados da Madeira
                    </Label>

                    <CheckboxesContainer>
                      <FormFieldCheckbox
                        name="planted"
                        label="Em pé (plantada)"
                      />
                      <FormFieldCheckbox name="cut" label="Cortada" />
                      <FormFieldCheckbox
                        name="futureCut"
                        label="Corte futuro"
                      />
                    </CheckboxesContainer>
                  </Box>
                </>
              )}
            </Flex>

            <Divider sx={{ marginTop: 'auto' }} />

            <Flex sx={{ justifyContent: 'center', gap: 8, paddingY: 7 }}>
              <Button outlined type="reset" size="md">
                Limpar
              </Button>

              <Button size="md" type="submit">
                Aplicar
              </Button>
            </Flex>
          </Flex>
        </Form>
      </Flex>
    </Modal>
  );
};
